import { GameLayout } from "./shared/GameLayout";
import {
  GameArt,
  PersonalOutcomeLine,
  SubmitButton,
  draftScope,
} from "./shared/PresenterParts";
import { useDraft } from "./shared/drafts";
import { personalOutcome } from "./outcome";
import { Die } from "./shared/Die";
import { playerName, type PlayProps } from "./types";
import { publicTableModel } from "./public-presentation";
import { TableRoster } from "./shared/TableRoster";
import { validDiceBid } from "./dice-bid";
export function LiarDice(props: PlayProps) {
  const { state, send, isDisplay } = props,
    g = state.game,
    p = g.public,
    s = g.private,
    players = p.players ?? [],
    n = (id: string) => playerName(players, id),
    can = (a: string) =>
      !isDisplay && !state.isSpectator && g.legalActions.includes(a),
    // Phase-scoped drafts: a new committed bid is a new phase, so the editor
    // starts again from the new legal minimum.
    scope = draftScope(state),
    [quantity, setQuantity] = useDraft<number>(
      scope,
      "bid:quantity",
      s.minimumBid?.quantity ?? 1,
    ),
    [face, setFace] = useDraft<number>(scope, "bid:face", s.minimumBid?.face ?? 2),
    [confirm, setConfirm] = useDraft(scope, "challenge:confirm", false),
    bid = p.currentBid,
    reveal = ["reveal", "finished"].includes(g.phase) ? p.lastReveal : null;
  const minimum = s.minimumBid ?? { quantity: 1, face: 1 };
  const valid = validDiceBid(
    quantity,
    face,
    minimum,
    s.maxQuantity,
    s.allowedFaces ?? [],
  );
  const ownHand =
    !isDisplay &&
    !state.isSpectator &&
    g.phase === "bidding" &&
    players.find((v: any) => v.id === state.playerId)?.alive !== false &&
    s.dice?.length > 0;
  const acting = can("bid") || can("challenge");
  const turnTitle = `${n(p.turnPlayerId)}’s turn.`;
  const title = g.finished
    ? "One cup remains."
    : reveal
      ? `${reveal.actualCount} ${reveal.actualCount === 1 ? "die" : "dice"} matched.`
      : acting
        ? bid
          ? "Raise or call."
          : "Make the opening bid."
        : turnTitle;
  const outcome = g.finished
    ? `${(g.winner as { playerIds?: string[] })?.playerIds?.map(n).join(", ")} wins.`
    : reveal
      ? `${n(reveal.loserId)} loses a die${reveal.eliminated ? " and is out" : ""}.`
      : null;
  const bidding = g.phase === "bidding";
  const shownBid = reveal?.bid ?? bid;
  const bidCard = (
    <div
      className={`dice-bid-card${shownBid ? "" : " is-empty"}`}
      data-tour="bid-card"
    >
      {shownBid ? (
        <>
          <span className="sr-only">
            {reveal ? "Challenged bid" : "Current bid"} · {n(shownBid.playerId)}
          </span>
          <strong className="dice-bid-value" aria-hidden="true">
            {shownBid.quantity}
            <span className="dice-bid-times">×</span>
            <Die value={shownBid.face} />
          </strong>
          <span className="dice-bid-by" aria-hidden="true">
            {reveal
              ? `${n(shownBid.playerId)}’s bid · called by ${n(reveal.challengerId)}`
              : `Bid by ${n(shownBid.playerId)}`}
          </span>
          <span className="sr-only">
            {shownBid.quantity} × {shownBid.face}
          </span>
        </>
      ) : (
        <>
          <strong className="dice-bid-value is-empty">No bid yet</strong>
          <span className="dice-bid-by">The first bid opens the round</span>
        </>
      )}
      <span className="dice-bid-meta">
        {p.totalDice} {p.totalDice === 1 ? "die" : "dice"} in play ·{" "}
        {p.onesWild ? "Ones are wild" : "Exact faces"}
      </span>
    </div>
  );
  return (
    <GameLayout
      {...props}
      composition={bidding ? "stacked" : "split"}
      privatePresentation="adaptive-inline"
      privateLabel="Your dice"
      privateScope="round"
      privateCoverNote={g.round > 1 ? "New round — dice re-rolled" : undefined}
      privateInlineContent={
        <div
          className="dice-row dice-tray"
          aria-label="Your dice"
          data-tour="dice-hand"
        >
          {(s.dice ?? []).map((v: number, i: number) => (
            <Die key={i} value={v} />
          ))}
        </div>
      }
      phaseLabel={g.finished ? "Result" : `Round ${g.round}`}
      title={title}
      publicTitle={g.phase === "bidding" ? turnTitle : title}
      description={outcome}
      publicDescription={outcome}
      board={
        <>
          <div className="dice-hero">
            {bidCard}
            <GameArt kind="liar-dice" variant="stamp" />
          </div>
          {bidding ? (
            <TableRoster
              model={publicTableModel(g, state.players ?? [])}
              viewerId={state.playerId}
            />
          ) : null}
          {bidding && p.bidHistory?.length ? (
            <ol
              className="dice-recent"
              aria-label="Recent bids"
              data-tour="bid-history"
            >
              {p.bidHistory.slice(-3).map((b: any, i: number) => (
                <li key={i}>
                  <span>{n(b.playerId)}</span>
                  <strong>
                    {b.quantity} × <Die value={b.face} />
                  </strong>
                </li>
              ))}
            </ol>
          ) : null}
          {g.finished ? (
            <PersonalOutcomeLine
              outcome={personalOutcome(
                g,
                state.playerId,
                isDisplay || state.isSpectator,
              )}
            />
          ) : null}
          {reveal ? (
            <>
              <div
                className={`play-reveal-rolls${reveal.rolls.length > 4 ? " is-many" : ""}`}
                data-tour="reveal"
              >
                {reveal.rolls.map((v: any) => {
                  const matches = v.dice.filter(
                    (face: number) =>
                      face === reveal.bid.face || (p.onesWild && face === 1),
                  ).length;
                  return (
                  <div
                    key={v.playerId}
                    className={v.playerId === reveal.loserId ? "is-loser" : ""}
                  >
                    <span className="roll-name">
                      <strong>{n(v.playerId)}</strong>
                      {v.playerId === state.playerId &&
                      !isDisplay &&
                      !state.isSpectator ? (
                        <small>you</small>
                      ) : null}
                      {v.playerId === reveal.loserId ? (
                        <small className="roll-tag">loses a die</small>
                      ) : null}
                    </span>
                    <span className="roll-count" aria-label={`${matches} matching`}>
                      {matches}
                    </span>
                    <div className="dice-row">
                      {v.dice.map((face: number, i: number) => (
                        <Die
                          key={i}
                          value={face}
                          match={
                            face === reveal.bid.face ||
                            (p.onesWild && face === 1)
                          }
                        />
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
              <p className="play-board-caption">
                Outlined dice match the bid · the number is each cup’s matches.
              </p>
            </>
          ) : null}
        </>
      }
      action={
        can("bid") || can("challenge") ? (
          <>
            {can("bid") && !confirm ? (
              <form
                className="dice-editor"
                data-tour="bid-editor"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (valid) send({ type: "bid", quantity, face });
                }}
              >
                <span className="dice-editor-label">Your bid · draft</span>
                <div className="dice-editor-row">
                  <div className="play-quantity" data-tour="bid-quantity">
                    <button
                      type="button"
                      className="secondary"
                      aria-label="Decrease quantity"
                      disabled={quantity <= 1}
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    >
                      −
                    </button>
                    <label>
                      <span className="sr-only">Quantity</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={s.maxQuantity}
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                      />
                    </label>
                    <button
                      type="button"
                      className="secondary"
                      aria-label="Increase quantity"
                      disabled={quantity >= s.maxQuantity}
                      onClick={() =>
                        setQuantity(Math.min(s.maxQuantity, quantity + 1))
                      }
                    >
                      +
                    </button>
                  </div>
                  <SubmitButton
                    className="primary dice-bid-submit"
                    data-tour="bid-submit"
                    disabled={!valid}
                  >
                    {valid ? (
                      <>
                        <span className="sr-only">{`Bid ${quantity} × ${face}`}</span>
                        <span className="dice-bid-cta" aria-hidden="true">
                          Bid {quantity} × <Die value={face} />
                        </span>
                      </>
                    ) : quantity > s.maxQuantity ? (
                      `At most ${s.maxQuantity}`
                    ) : (
                      `Raise to ${minimum.quantity} × ${minimum.face}+`
                    )}
                  </SubmitButton>
                </div>
                <div
                  className="play-face-choices"
                  data-tour="bid-face"
                  role="group"
                  aria-label="Bid face"
                >
                  {(s.allowedFaces ?? []).map((v: number) => (
                    <button
                      type="button"
                      key={v}
                      aria-label={`Bid on face ${v}`}
                      aria-pressed={face === v}
                      onClick={() => setFace(v)}
                    >
                      <Die value={v} />
                    </button>
                  ))}
                </div>
              </form>
            ) : null}
            {can("challenge") ? (
              confirm ? (
                <div className="play-confirm">
                  <p>
                    Challenge{" "}
                    <strong>
                      {bid?.quantity} × {bid?.face}
                    </strong>{" "}
                    from {n(bid?.playerId)} and reveal everyone’s dice?
                  </p>
                  <SubmitButton
                    type="button"
                    onClick={() => send({ type: "challenge" })}
                  >
                    Challenge
                  </SubmitButton>
                  <button
                    className="text-button"
                    onClick={() => setConfirm(false)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="secondary full dice-challenge"
                  data-tour="challenge"
                  onClick={() => setConfirm(true)}
                >
                  Call their bluff
                </button>
              )
            ) : null}
          </>
        ) : null
      }
      privateContent={
        ownHand ? (
          <div className="private-card-face">
            <span className="private-label">Your dice</span>
            <div className="dice-row dice-tray">
              {(s.dice ?? []).map((v: number, i: number) => (
                <Die key={i} value={v} />
              ))}
            </div>
          </div>
        ) : undefined
      }
    />
  );
}
